/**
 * DentalDiagnosisPanel — URS-F379.
 * Sub-pestaña "Diagnostico" del Odontograma (solo admin).
 * Detecta inconsistencias en dental_measurements y permite reparar.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { MagnifyingGlass, Wrench, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ISSUE_LABELS = {
  orphan_volunteers: 'Mediciones con volunteer_id inexistente',
  orphan_visits: 'Mediciones con visit_id inexistente',
  duplicate_current: 'Multiples versiones is_current=true para la misma celda',
  broken_chains: 'Cadenas de version rotas (superseded_by apunta a ID inexistente)',
  invalid_tooth: 'Mediciones con diente no configurado en el estudio',
  invalid_measurement: 'Mediciones con measurement_name no configurado',
  invalid_surface: 'Mediciones con superficie no configurada',
};

export default function DentalDiagnosisPanel({ studyId }) {
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [repairPreview, setRepairPreview] = useState(null);
  const [repairing, setRepairing] = useState(false);

  const runDiagnosis = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/studies/${studyId}/dental-diagnose`, { withCredentials: true });
      setDiagnostic(r.data);
      setRepairPreview(null);
      toast.success(`Diagnostico OK — ${r.data.issues_total} issues detectados`);
    } catch (e) {
      toast.error(errText(e, 'Error en diagnostico'));
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  const previewRepair = async () => {
    setRepairing(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/studies/${studyId}/dental-repair?dry_run=true`,
        {},
        { withCredentials: true },
      );
      setRepairPreview(r.data);
      toast.info(`Vista previa: ${r.data.actions_count} accion(es) planificada(s)`);
    } catch (e) {
      toast.error(errText(e, 'Error en preview'));
    } finally {
      setRepairing(false);
    }
  };

  const applyRepair = async () => {
    if (!window.confirm('¿Aplicar reparacion? Esta accion NO borra datos, solo marca versiones inconsistentes como is_current=false y limpia superseded_by rotos. Queda registro en audit trail.')) return;
    setRepairing(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/studies/${studyId}/dental-repair?dry_run=false`,
        {},
        { withCredentials: true },
      );
      toast.success(`Reparado: ${r.data.applied} accion(es) aplicada(s) de ${r.data.actions_count}`);
      setRepairPreview(null);
      await runDiagnosis();
    } catch (e) {
      toast.error(errText(e, 'Error al reparar'));
    } finally {
      setRepairing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="odonto-diagnosis-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-stone-800 flex items-center gap-2">
            <MagnifyingGlass size={18} className="text-[#1f3e80]" />
            Diagnostico de integridad
          </h3>
          <p className="text-xs text-stone-500 mt-0.5">
            Detecta inconsistencias en las mediciones dentales del estudio (huerfanos, duplicados, cadenas rotas, valores fuera de config).
          </p>
        </div>
        <Button onClick={runDiagnosis} disabled={loading} data-testid="odonto-run-diagnosis">
          {loading ? 'Analizando...' : 'Ejecutar diagnostico'}
        </Button>
      </div>

      {diagnostic && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 bg-stone-50 border border-stone-200 rounded-md">
              <p className="text-xs text-stone-500">Mediciones totales</p>
              <p className="text-2xl font-bold text-stone-800">{diagnostic.totals.measurements_all_versions}</p>
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
              <p className="text-xs text-emerald-700">Vigentes (is_current)</p>
              <p className="text-2xl font-bold text-emerald-800">{diagnostic.totals.measurements_current}</p>
            </div>
            <div className={`p-3 rounded-md border ${diagnostic.issues_total === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-xs ${diagnostic.issues_total === 0 ? 'text-emerald-700' : 'text-red-700'}`}>Issues detectados</p>
              <p className={`text-2xl font-bold ${diagnostic.issues_total === 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                {diagnostic.issues_total}
              </p>
            </div>
          </div>

          {diagnostic.issues_total === 0 ? (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md flex items-center gap-2 text-emerald-800">
              <CheckCircle size={20} weight="fill" />
              <span className="text-sm font-medium">Todo correcto. No se han detectado inconsistencias.</span>
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(diagnostic.issues).map(([key, list]) => {
                if (!list || list.length === 0) return null;
                return (
                  <div key={key} className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-start gap-2">
                      <WarningCircle size={18} className="text-red-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-800">
                          {ISSUE_LABELS[key] || key} — <span className="tabular-nums">{list.length}</span>
                        </p>
                        <details className="mt-1">
                          <summary className="text-xs text-red-600 cursor-pointer hover:underline">Ver detalles</summary>
                          <pre className="mt-2 text-[11px] bg-white p-2 rounded border border-red-100 overflow-x-auto max-h-40">{JSON.stringify(list, null, 2)}</pre>
                        </details>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" onClick={previewRepair} disabled={repairing} data-testid="odonto-preview-repair">
                  <Wrench size={14} className="mr-1" />
                  {repairing ? '...' : 'Vista previa reparacion'}
                </Button>
                {repairPreview && (
                  <Button onClick={applyRepair} disabled={repairing} className="bg-red-600 hover:bg-red-700 text-white" data-testid="odonto-apply-repair">
                    {repairing ? '...' : `Aplicar ${repairPreview.actions_count} accion(es)`}
                  </Button>
                )}
              </div>

              {repairPreview && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm font-semibold text-blue-800 mb-1">
                    Vista previa — {repairPreview.actions_count} accion(es):
                  </p>
                  <pre className="text-[11px] bg-white p-2 rounded border border-blue-100 overflow-x-auto max-h-60">
                    {JSON.stringify(repairPreview.actions, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
